import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AnalyticsPolicy } from '@/config/policies';

// Event types for comprehensive tracking
// Declare dataLayer for GTM integration
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

export const AnalyticsEvents = {
  // Deal Builder Funnel
  DEAL_BUILDER_START: 'deal_builder_start',
  DEAL_BUILDER_VEHICLE_SELECTED: 'deal_builder_vehicle_selected',
  DEAL_BUILDER_PAYMENT_TYPE_SELECTED: 'deal_builder_payment_type_selected',
  DEAL_BUILDER_TERMS_CHANGED: 'deal_builder_terms_changed',
  DEAL_BUILDER_SAVED: 'deal_builder_saved',
  DEAL_BUILDER_SUBMITTED: 'deal_builder_submitted',
  
  // Offer Interactions
  OFFER_VIEWED: 'offer_viewed',
  OFFER_ACCEPTED: 'offer_accepted',
  OFFER_REJECTED: 'offer_rejected',
  COUNTER_OFFER_SENT: 'counter_offer_sent',
  
  // Outbound Clicks
  OUTBOUND_CLICK_AFFILIATE: 'outbound_click_affiliate',
  OUTBOUND_CLICK_DEALER_SITE: 'outbound_click_dealer_site',
  OUTBOUND_CLICK_CALL: 'outbound_click_call',
  OUTBOUND_CLICK_EMAIL: 'outbound_click_email',
  
  // Content Engagement (Media)
  ARTICLE_VIEW: 'article_view',
  ARTICLE_SCROLL_DEPTH: 'article_scroll_depth',
  VIDEO_PLAY: 'video_play',
  VIDEO_COMPLETE: 'video_complete',
  REVIEW_VIEW: 'review_view',
  EVENT_VIEW: 'event_view',
  
  // Navigation
  PAGE_VIEW: 'page_view',
  SEARCH_PERFORMED: 'search_performed',
  FILTER_APPLIED: 'filter_applied',
  
  // Conversions
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  DEALER_CONTACT_INITIATED: 'dealer_contact_initiated',
  DEAL_CONFIRMED: 'deal_confirmed',
  
  // Account Conversions (GA4 priority events)
  BUYER_ACCOUNT_CREATED: 'buyer_account_created',
  DEALER_PARTNER_REQUEST: 'dealer_partner_request',
} as const;

export type AnalyticsEventType = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

interface EventProperties {
  // Common
  session_id?: string;
  page_path?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  
  // Deal specific
  deal_id?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: number;
  vehicle_trim?: string;
  payment_type?: 'finance' | 'lease';
  proposed_monthly?: number;
  msrp?: number;
  
  // Content specific
  article_id?: string;
  article_slug?: string;
  article_category?: string;
  review_id?: string;
  video_id?: string;
  event_id?: string;
  
  // Outbound
  destination_url?: string;
  dealer_id?: string;
  dealer_name?: string;
  click_type?: string;
  
  // Funnel position
  funnel_step?: string;
  step_number?: number;
  
  // Engagement
  scroll_depth?: number;
  time_on_page?: number;
  
  // Search
  search_query?: string;
  results_count?: number;
  
  // Arbitrary additional properties
  [key: string]: unknown;
}

// Generate a session ID that persists for the browser session
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

// Parse UTM parameters from URL
function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
    const value = params.get(param);
    if (value) utmParams[param] = value;
  });
  return utmParams;
}

// =============================================================================
// BATCHED ANALYTICS - Fire-and-forget with deduplication
// Uses AnalyticsPolicy from src/config/policies.ts as single source of truth
// =============================================================================

interface QueuedEvent {
  event_type: string;
  user_id: string | null;
  session_id: string;
  page_path: string;
  referrer: string | null;
  user_agent: string;
  timestamp: string;
  properties: Record<string, unknown>;
}

// Global event queue and flush timer
let eventQueue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Use policy values instead of hardcoded constants
const FLUSH_INTERVAL_MS = AnalyticsPolicy.BATCH.FLUSH_INTERVAL_MS;
const MAX_QUEUE_SIZE = AnalyticsPolicy.BATCH.MAX_QUEUE_SIZE;
const DEDUPE_WINDOW_MS = AnalyticsPolicy.DEDUPE.WINDOW_MS;
const MAX_DEDUPE_CACHE_SIZE = AnalyticsPolicy.DEDUPE.MAX_CACHE_SIZE;

// Dedupe tracking - prevent duplicate events within short window
const recentEvents = new Map<string, number>();

function getEventKey(eventType: string, pagePath: string): string {
  return `${eventType}:${pagePath}`;
}

function isDuplicate(eventType: string, pagePath: string): boolean {
  const key = getEventKey(eventType, pagePath);
  const lastTime = recentEvents.get(key);
  const now = Date.now();
  
  if (lastTime && now - lastTime < DEDUPE_WINDOW_MS) {
    return true;
  }
  
  recentEvents.set(key, now);
  
  // Clean up old entries periodically - use policy max cache size
  if (recentEvents.size > MAX_DEDUPE_CACHE_SIZE) {
    const cutoff = now - DEDUPE_WINDOW_MS;
    for (const [k, t] of recentEvents) {
      if (t < cutoff) recentEvents.delete(k);
    }
  }
  
  return false;
}

function flushQueue() {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  // Send to a same-origin backend function that ACKs fast
  const url = `/api/functions/analytics-ingest`;
  const payload = JSON.stringify({ events: eventsToSend });

  // Best effort: sendBeacon when available (no headers)
  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    const ok = navigator.sendBeacon(url, blob);
    if (ok) return;
  }

  // Fallback: keepalive fetch; never await
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}

function queueEvent(event: QueuedEvent) {
  eventQueue.push(event);
  
  // Flush immediately if queue is full
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = null;
    flushQueue();
    return;
  }
  
  // Otherwise schedule a flush
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushQueue();
    }, FLUSH_INTERVAL_MS);
  }
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushQueue);
  window.addEventListener('pagehide', flushQueue);
}

// =============================================================================
// ANALYTICS HOOK
// =============================================================================

export function useAnalytics() {
  const { user } = useAuth();
  const sessionId = useRef(getSessionId());
  const pageEntryTime = useRef(Date.now());

  // Track page view on mount
  useEffect(() => {
    pageEntryTime.current = Date.now();
  }, []);

  // PERFORMANCE: Fire-and-forget analytics with batching and deduplication
  // Never blocks UI, batches events, dedupes rapid-fire calls
  const track = useCallback((
    eventType: AnalyticsEventType,
    properties: EventProperties = {}
  ) => {
    const pagePath = window.location.pathname;
    
    // Dedupe: skip if same event on same page within 1 second
    if (isDuplicate(eventType, pagePath)) {
      return;
    }
    
    try {
      const utmParams = getUtmParams();
      const eventData: QueuedEvent = {
        event_type: eventType,
        user_id: user?.id || null,
        session_id: sessionId.current,
        page_path: pagePath,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        properties: {
          ...utmParams,
          ...properties,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
        },
      };

      // Push to GTM dataLayer for GA4 tracking
      if (typeof window !== 'undefined' && window.dataLayer) {
        const gtmPayload = {
          event: eventType,
          ...properties,
          page_path: pagePath,
          user_id: user?.id || undefined,
          session_id: sessionId.current,
        };
        if (import.meta.env.DEV) {
          console.log(`[GTM Debug] ${eventType} pushed`, gtmPayload);
        }
        window.dataLayer.push(gtmPayload);
      }

      // Queue event for batched sending to internal analytics
      queueEvent(eventData);
    } catch {
      // Silent fail for analytics
    }
  }, [user?.id]);

  // Convenience methods for common events
  const trackPageView = useCallback((pagePath?: string, pageTitle?: string) => {
    track(AnalyticsEvents.PAGE_VIEW, {
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title,
    });
  }, [track]);

  const trackDealBuilderStart = useCallback((vehicleInfo?: Partial<EventProperties>) => {
    track(AnalyticsEvents.DEAL_BUILDER_START, {
      funnel_step: 'start',
      step_number: 1,
      ...vehicleInfo,
    });
  }, [track]);

  const trackDealBuilderVehicleSelected = useCallback((
    make: string, 
    model: string, 
    year: number, 
    trim?: string,
    msrp?: number
  ) => {
    track(AnalyticsEvents.DEAL_BUILDER_VEHICLE_SELECTED, {
      vehicle_make: make,
      vehicle_model: model,
      vehicle_year: year,
      vehicle_trim: trim,
      msrp,
      funnel_step: 'vehicle_selected',
      step_number: 2,
    });
  }, [track]);

  const trackDealBuilderPaymentTypeSelected = useCallback((paymentType: 'finance' | 'lease') => {
    track(AnalyticsEvents.DEAL_BUILDER_PAYMENT_TYPE_SELECTED, {
      payment_type: paymentType,
      funnel_step: 'payment_type_selected',
      step_number: 3,
    });
  }, [track]);

  const trackDealBuilderTermsChanged = useCallback((changes: Record<string, unknown>) => {
    track(AnalyticsEvents.DEAL_BUILDER_TERMS_CHANGED, {
      funnel_step: 'terms_modified',
      ...changes,
    });
  }, [track]);

  const trackDealSaved = useCallback((dealId: string, vehicleInfo: Partial<EventProperties>) => {
    track(AnalyticsEvents.DEAL_BUILDER_SAVED, {
      deal_id: dealId,
      funnel_step: 'saved',
      step_number: 4,
      ...vehicleInfo,
    });
  }, [track]);

  const trackDealSubmitted = useCallback((
    dealId: string, 
    vehicleInfo: Partial<EventProperties>,
    proposedMonthly?: number
  ) => {
    track(AnalyticsEvents.DEAL_BUILDER_SUBMITTED, {
      deal_id: dealId,
      proposed_monthly: proposedMonthly,
      funnel_step: 'submitted',
      step_number: 5,
      ...vehicleInfo,
    });
  }, [track]);

  const trackOfferViewed = useCallback((dealId: string, dealerId?: string, dealerName?: string) => {
    track(AnalyticsEvents.OFFER_VIEWED, {
      deal_id: dealId,
      dealer_id: dealerId,
      dealer_name: dealerName,
      funnel_step: 'offer_viewed',
      step_number: 6,
    });
  }, [track]);

  const trackOfferAccepted = useCallback((dealId: string, dealerId?: string) => {
    track(AnalyticsEvents.OFFER_ACCEPTED, {
      deal_id: dealId,
      dealer_id: dealerId,
      funnel_step: 'offer_accepted',
      step_number: 7,
    });
  }, [track]);

  const trackOutboundClick = useCallback((
    clickType: 'affiliate' | 'dealer_site' | 'call' | 'email',
    destinationUrl: string,
    additionalProps?: Partial<EventProperties>
  ) => {
    const eventMap = {
      affiliate: AnalyticsEvents.OUTBOUND_CLICK_AFFILIATE,
      dealer_site: AnalyticsEvents.OUTBOUND_CLICK_DEALER_SITE,
      call: AnalyticsEvents.OUTBOUND_CLICK_CALL,
      email: AnalyticsEvents.OUTBOUND_CLICK_EMAIL,
    };
    track(eventMap[clickType], {
      destination_url: destinationUrl,
      click_type: clickType,
      ...additionalProps,
    });
  }, [track]);

  const trackArticleView = useCallback((
    articleId: string, 
    articleSlug: string, 
    category?: string
  ) => {
    track(AnalyticsEvents.ARTICLE_VIEW, {
      article_id: articleId,
      article_slug: articleSlug,
      article_category: category,
    });
  }, [track]);

  const trackScrollDepth = useCallback((depth: number, contentType: 'article' | 'review') => {
    track(AnalyticsEvents.ARTICLE_SCROLL_DEPTH, {
      scroll_depth: depth,
      content_type: contentType,
      time_on_page: Date.now() - pageEntryTime.current,
    });
  }, [track]);

  const trackSearch = useCallback((query: string, resultsCount: number) => {
    track(AnalyticsEvents.SEARCH_PERFORMED, {
      search_query: query,
      results_count: resultsCount,
    });
  }, [track]);

  return {
    track,
    trackPageView,
    trackDealBuilderStart,
    trackDealBuilderVehicleSelected,
    trackDealBuilderPaymentTypeSelected,
    trackDealBuilderTermsChanged,
    trackDealSaved,
    trackDealSubmitted,
    trackOfferViewed,
    trackOfferAccepted,
    trackOutboundClick,
    trackArticleView,
    trackScrollDepth,
    trackSearch,
    AnalyticsEvents,
  };
}

// Hook for tracking scroll depth
export function useScrollDepthTracking(contentType: 'article' | 'review' = 'article') {
  const { trackScrollDepth } = useAnalytics();
  const trackedDepths = useRef(new Set<number>());

  useEffect(() => {
    // Use scroll milestones from AnalyticsPolicy
    const milestones = AnalyticsPolicy.SCROLL_MILESTONES;
    
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const depth = Math.round((scrolled / scrollHeight) * 100);
      
      milestones.forEach(milestone => {
        if (depth >= milestone && !trackedDepths.current.has(milestone)) {
          trackedDepths.current.add(milestone);
          trackScrollDepth(milestone, contentType);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackScrollDepth, contentType]);
}

// Outbound link tracking component wrapper
export function useOutboundLinkTracking() {
  const { trackOutboundClick } = useAnalytics();

  const handleOutboundClick = useCallback((
    e: React.MouseEvent<HTMLAnchorElement>,
    clickType: 'affiliate' | 'dealer_site' | 'call' | 'email',
    additionalProps?: Partial<EventProperties>
  ) => {
    const href = e.currentTarget.href;
    trackOutboundClick(clickType, href, additionalProps);
  }, [trackOutboundClick]);

  return { handleOutboundClick };
}
